/**
 * Shared prediction utilities used by both the web and mobile packages.
 */

export const PREDICTION_TYPES = {
    SERVICE_AIR_FILTER: 'service_air_filter',
    BATTERY_DEGRADATION_DETECTED: 'battery_degradation_detected',
    REPLACE_BATTERY_WARNING: 'replace_battery_warning',
    SERVICE_ALUMINUM_ELECTROLYTIC_CAPACITORS: 'service_aluminum_electrolytic_capacitors',
    SERVICE_FILM_CAPACITOR: 'service_film_capacitor',
    RENEW_SERVICE_CONTRACT: 'renew_service_contract',
    SERVICE_FANS: 'service_fans',
    ENVIRONMENTAL_STRESS_MONITORING: 'environmental_stress_monitoring',
} as const;

export type PredictionTypeKey = (typeof PREDICTION_TYPES)[keyof typeof PREDICTION_TYPES];

export type UpcomingDevicePropertyKey =
    | 'deviceName'
    | 'serialNumber'
    | 'deviceModel'
    | 'lastServiceDate'
    | 'installationDate'
    | 'currentStatus'
    | 'contractEndDate';

export type UpcomingDevicePropertyRowConfig = {
    key: UpcomingDevicePropertyKey;
    translationKey: string;
    aliases: string[];
    isDate?: boolean;
};

export type UpcomingPredictionData = Record<string, unknown> & {
    deviceProperties?: Record<string, unknown>;
};

const UPCOMING_COMPONENT_PROPERTY_TRANSLATION_KEYS: ReadonlyArray<readonly [string, string]> = [
    ['make', 'UPCOMING:MAKE'],
    ['model', 'UPCOMING:MODEL'],
    ['installation_date', 'UPCOMING:INSTALLATION_DATE'],
    ['strings', 'UPCOMING:STRINGS'],
    ['batteries', 'UPCOMING:BATTERIES'],
    ['age', 'UPCOMING:AGE'],
    ['expected_life', 'UPCOMING:EXPECTED_LIFE'],
    ['calculated_age', 'UPCOMING:CALCULATED_AGE'],
    ['fan_banks', 'UPCOMING:FAN_BANKS'],
    ['fans', 'UPCOMING:FANS'],
    ['number_of_filters', 'UPCOMING:NUMBER_OF_FILTERS'],
    ['type', 'UPCOMING:TYPE'],
];

export const getUpcomingComponentPropertyTranslationKey = (label: string): string | undefined => {
    const normalizedLabel = label.trim().toLowerCase().replace(/\s+/g, '_');
    return UPCOMING_COMPONENT_PROPERTY_TRANSLATION_KEYS.find(([entryLabel]) => entryLabel === normalizedLabel)?.[1];
};

export const UPCOMING_DEVICE_PROPERTY_ROWS: Record<UpcomingDevicePropertyKey, UpcomingDevicePropertyRowConfig> = {
    deviceName: { key: 'deviceName', translationKey: 'UPCOMING:NAME', aliases: ['deviceName', 'name'] },
    serialNumber: { key: 'serialNumber', translationKey: 'UPCOMING:SERIAL_NUMBER', aliases: ['serialNumber'] },
    deviceModel: {
        key: 'deviceModel',
        translationKey: 'UPCOMING:MODEL',
        aliases: ['deviceModel', 'deviceModal', 'model'],
    },
    lastServiceDate: {
        key: 'lastServiceDate',
        translationKey: 'UPCOMING:LAST_SERVICE',
        aliases: ['lastServiceDate', 'lastService'],
        isDate: true,
    },
    installationDate: {
        key: 'installationDate',
        translationKey: 'UPCOMING:INSTALLATION_DATE',
        aliases: ['installationDate', 'deviceInstallDate'],
        isDate: true,
    },
    currentStatus: { key: 'currentStatus', translationKey: 'UPCOMING:CURRENT_STATUS', aliases: ['currentStatus'] },
    contractEndDate: {
        key: 'contractEndDate',
        translationKey: 'UPCOMING:WARRANTY_CONTRACT_END',
        aliases: ['contractEndDate', 'warrantyContractEnd'],
        isDate: true,
    },
};

const ALUMINUM_CAPACITOR_PROPERTY_KEYS: UpcomingDevicePropertyKey[] = [
    'deviceName',
    'serialNumber',
    'deviceModel',
    'lastServiceDate',
    'installationDate',
];

const SERVICE_WITH_LAST_SERVICE_PROPERTY_KEYS: UpcomingDevicePropertyKey[] = [
    'deviceName',
    'serialNumber',
    'deviceModel',
    'lastServiceDate',
    'currentStatus',
    'contractEndDate',
];

const SERVICE_CONTRACT_PROPERTY_KEYS: UpcomingDevicePropertyKey[] = [
    'deviceName',
    'serialNumber',
    'deviceModel',
    'currentStatus',
    'contractEndDate',
];

const UPCOMING_DEVICE_PROPERTY_KEYS_BY_TYPE: Record<string, UpcomingDevicePropertyKey[]> = {
    [PREDICTION_TYPES.SERVICE_ALUMINUM_ELECTROLYTIC_CAPACITORS]: ALUMINUM_CAPACITOR_PROPERTY_KEYS,
    [PREDICTION_TYPES.SERVICE_AIR_FILTER]: SERVICE_WITH_LAST_SERVICE_PROPERTY_KEYS,
    [PREDICTION_TYPES.RENEW_SERVICE_CONTRACT]: SERVICE_CONTRACT_PROPERTY_KEYS,
    [PREDICTION_TYPES.BATTERY_DEGRADATION_DETECTED]: SERVICE_WITH_LAST_SERVICE_PROPERTY_KEYS,
    [PREDICTION_TYPES.SERVICE_FANS]: SERVICE_CONTRACT_PROPERTY_KEYS,
};

const DEFAULT_UPCOMING_DEVICE_PROPERTY_KEYS = ALUMINUM_CAPACITOR_PROPERTY_KEYS;

const PREDICTION_TYPE_BY_LABEL: ReadonlyArray<readonly [string, PredictionTypeKey]> = [
    ['service_air_filters', PREDICTION_TYPES.SERVICE_AIR_FILTER],
    ['service_air_filter', PREDICTION_TYPES.SERVICE_AIR_FILTER],
    ['battery_degradation_detected', PREDICTION_TYPES.BATTERY_DEGRADATION_DETECTED],
    ['replace_battery_warning', PREDICTION_TYPES.REPLACE_BATTERY_WARNING],
    ['service_aluminum_electrolytic_capacitor', PREDICTION_TYPES.SERVICE_ALUMINUM_ELECTROLYTIC_CAPACITORS],
    ['service_aluminum_electrolytic_capacitors', PREDICTION_TYPES.SERVICE_ALUMINUM_ELECTROLYTIC_CAPACITORS],
    ['service_film_capacitor', PREDICTION_TYPES.SERVICE_FILM_CAPACITOR],
    ['service_film_capacitors', PREDICTION_TYPES.SERVICE_FILM_CAPACITOR],
    ['renew_service_contract', PREDICTION_TYPES.RENEW_SERVICE_CONTRACT],
    ['service_fans', PREDICTION_TYPES.SERVICE_FANS],
    ['environmental_stress_monitoring', PREDICTION_TYPES.ENVIRONMENTAL_STRESS_MONITORING],
    ['esm', PREDICTION_TYPES.ENVIRONMENTAL_STRESS_MONITORING],
];

/**
 * Resolves a canonical prediction-type key from either the raw `predictionType`
 * field (preferred) or the human-readable `type` label returned by the API.
 *
 * Equivalent logic previously existed in:
 *  - packages/web/src/AdminPortal/UpcomingEventsDetails/UpcomingEventsDetails.tsx
 *  - packages/web/src/AdminPortal/UpcomingEventsDetails/Evidence.tsx
 */
export const normalizePredictionType = (predictionType?: string, typeLabel?: string): string => {
    if (predictionType) {
        return predictionType;
    }

    const normalizedTypeLabel = (typeLabel ?? '').trim().toLowerCase().replace(/\s+/g, '_');

    return PREDICTION_TYPE_BY_LABEL.find(([label]) => label === normalizedTypeLabel)?.[1] ?? normalizedTypeLabel;
};

export const getUpcomingDevicePropertyRowConfigs = (predictionType?: string): UpcomingDevicePropertyRowConfig[] => {
    const normalizedPredictionType = normalizePredictionType(undefined, predictionType);
    const propertyKeys =
        UPCOMING_DEVICE_PROPERTY_KEYS_BY_TYPE[normalizedPredictionType] ?? DEFAULT_UPCOMING_DEVICE_PROPERTY_KEYS;

    return propertyKeys.map((key) => UPCOMING_DEVICE_PROPERTY_ROWS[key]);
};

export const hasUpcomingDevicePropertyValue = (value: unknown): boolean =>
    value !== undefined && value !== null && String(value).trim().length > 0;

export const getUpcomingDevicePropertyValue = (
    prediction: UpcomingPredictionData | undefined,
    aliases: string[]
): unknown => {
    const deviceProperties = prediction?.deviceProperties;
    const sources = [deviceProperties, prediction].filter(
        (source): source is Record<string, unknown> => Boolean(source) && typeof source === 'object'
    );

    for (const source of sources) {
        const matchedAlias = aliases.find((alias) => hasUpcomingDevicePropertyValue(source[alias]));
        if (matchedAlias) {
            return source[matchedAlias];
        }
    }
    return undefined;
};

/**
 * Formats a prediction-related date value to a locale date string.
 * Returns 'N/A' when the value is absent or not a valid date.
 */
export const formatPredictionDate = (value: unknown): string => {
    if (!value) return 'N/A';
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString();
};

/** Canonical status colours shared by web and mobile. */
export const PREDICTION_STATUS_COLORS: Record<string, string> = {
    OPEN: '#F0AA1F',
    ACKNOWLEDGED: '#E2721A',
    CLOSED: '#879196',
};
